import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Grid,
    Skeleton,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../MicrospecDynamicUI/GlobalProvider';
import { getDataFromCollectionWithQuery, getDocById } from '../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessProject } from '../../MicrospecDynamicUI/Models/Business/BusinessProject';
import { useMicrospecThemeLanguageContext } from '../../MicrospecDynamicUI/Themes';
import { PROJECTS_COLLECTION } from '../constants/collectionPaths';

const WHATSAPP_NUMBER = '972542421677';
const VIDEO_RE = /\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i;
const isVideo = (url: string) => VIDEO_RE.test(url);

// ── Media viewer (image or video) ────────────────────────────────────────────

interface MediaViewerProps {
    src: string;
    alt: string;
    height?: number | string;
}

const MediaViewer: React.FC<MediaViewerProps> = ({ src, alt, height = 480 }) => {
    const theme = useTheme();
    const accentColor = theme.palette.primary.main;

    if (isVideo(src)) {
        return (
            <Box
                key={src}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height,
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    boxShadow: `0 8px 32px ${alpha(accentColor, 0.15)}`,
                }}
            >
                <Box
                    component="video"
                    src={src}
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            </Box>
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
                width: '100%',
                height,
                objectFit: 'cover',
                borderRadius: 3,
                boxShadow: `0 8px 32px ${alpha(accentColor, 0.15)}`,
                display: 'block',
            }}
        />
    );
};

// ── Thumbnail strip item ──────────────────────────────────────────────────────

interface ThumbProps {
    src: string;
    selected: boolean;
    onClick: () => void;
    accentColor: string;
}

const Thumb: React.FC<ThumbProps> = ({ src, selected, onClick, accentColor }) => (
    <Box
        onClick={onClick}
        sx={{
            position: 'relative',
            width: 90,
            height: 64,
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'pointer',
            flexShrink: 0,
            border: `2px solid ${selected ? accentColor : 'rgba(0,0,0,0.12)'}`,
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: accentColor },
        }}
    >
        {isVideo(src) ? (
            <>
                <Box
                    component="video"
                    src={src}
                    muted
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <Box sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.3)',
                }}>
                    <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
            </>
        ) : (
            <Box
                component="img"
                src={src}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
        )}
    </Box>
);

// ── Page ─────────────────────────────────────────────────────────────────────

const ProjectDetailPage: React.FC = () => {
    const { project_id } = useParams<{ project_id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { translate } = localizationProvider;
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const db = cloudProvider.firestore?.instance;

    const accentColor = theme.palette.primary.main;

    const [project, setProject] = useState<BusinessProject | null>(null);
    const [selectedMedia, setSelectedMedia] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [relatedProjects, setRelatedProjects] = useState<BusinessProject[]>([]);

    useEffect(() => {
        if (!db || !project_id) return;

        const fetchAll = async () => {
            setLoading(true);
            try {
                const [projectData, allProjects] = await Promise.all([
                    getDocById(null, null, db, PROJECTS_COLLECTION, project_id, false),
                    getDataFromCollectionWithQuery(null, null, db, PROJECTS_COLLECTION, [], true, 6),
                ]);

                if (!projectData) {
                    setError(translate('project.projectNotFound') ?? 'פרויקט לא נמצא');
                    return;
                }

                const p = projectData as BusinessProject;
                setProject(p);
                setSelectedMedia((p.mediaUrls ?? [])[0] ?? null);

                const related = (allProjects as BusinessProject[])
                    .filter(r => r.id !== project_id && r.is_published !== false)
                    .slice(0, 4);
                setRelatedProjects(related);
            } catch (err: any) {
                setError(err?.message ?? translate('common.failedToLoad'));
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, project_id]);

    const projectName = project ? (translate(project.name as any) ?? '') : '';
    const projectDescription = project?.description ? (translate(project.description as any) ?? '') : '';

    const whatsappMessage = encodeURIComponent(
        `שלום, הגעתי אליך מהדף של הפרויקט: ${projectName}`,
    );
    const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

    // ── Loading skeleton ──────────────────────────────────────────────────────
    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
                <Grid container spacing={5}>
                    <Grid item xs={12} md={7}>
                        <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 3 }} animation="wave" />
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                            {[0, 1, 2, 3].map(i => (
                                <Skeleton key={i} variant="rectangular" width={90} height={64} sx={{ borderRadius: 2 }} animation="wave" />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Skeleton variant="text" width="70%" height={52} animation="wave" />
                        <Skeleton variant="text" width="100%" height={20} animation="wave" sx={{ mt: 3 }} />
                        <Skeleton variant="text" width="90%" height={20} animation="wave" />
                        <Skeleton variant="text" width="80%" height={20} animation="wave" />
                        <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 2, mt: 4 }} animation="wave" />
                    </Grid>
                </Grid>
            </Container>
        );
    }

    if (error || !project) {
        return (
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Alert severity="error">{error ?? (translate('project.projectNotFound') ?? 'פרויקט לא נמצא')}</Alert>
                <Button startIcon={<ArrowBackIosNewIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                    {translate('project.backToProjects') ?? 'חזרה לפרויקטים'}
                </Button>
            </Container>
        );
    }

    const mediaUrls = project.mediaUrls ?? [];

    return (
        <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="lg">
                {/* Breadcrumbs */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <RouterLink to="/" style={{ color: 'inherit', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                        {translate('common.home') ?? 'ראשי'}
                    </RouterLink>
                    <RouterLink to="/projects" style={{ color: 'inherit', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                        {translate('common.projects') ?? 'פרויקטים'}
                    </RouterLink>
                    <Typography variant="body2" color="text.primary" aria-current="page">
                        {projectName}
                    </Typography>
                </Breadcrumbs>

                <Grid container spacing={{ xs: 4, md: 6 }}>
                    {/* ── Left: media gallery ── */}
                    <Grid item xs={12} md={7}>
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                        >
                            {/* Main viewer */}
                            {selectedMedia ? (
                                <MediaViewer src={selectedMedia} alt={projectName} height={{ xs: 260, sm: 380, md: 480 } as any} />
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: { xs: 260, sm: 380, md: 480 },
                                        borderRadius: 3,
                                        background: `linear-gradient(135deg, ${alpha(accentColor, 0.15)} 0%, ${alpha(accentColor, 0.35)} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 80, opacity: 0.4 }}>🪵</Typography>
                                </Box>
                            )}

                            {/* Thumbnail strip */}
                            {mediaUrls.length > 1 && (
                                <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                                    {mediaUrls.map((url, i) => (
                                        <Thumb
                                            key={i}
                                            src={url}
                                            selected={selectedMedia === url}
                                            onClick={() => setSelectedMedia(url)}
                                            accentColor={accentColor}
                                        />
                                    ))}
                                </Box>
                            )}
                        </motion.div>
                    </Grid>

                    {/* ── Right: project info ── */}
                    <Grid item xs={12} md={5}>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
                        >
                            {/* Name */}
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    lineHeight: 1.3,
                                    color: theme.palette.text.primary,
                                    fontSize: { xs: '1.6rem', md: '2rem' },
                                    mb: 3,
                                }}
                            >
                                {projectName}
                            </Typography>

                            {/* Description */}
                            {projectDescription && (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.9,
                                        mb: 4,
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {projectDescription}
                                </Typography>
                            )}

                            {/* CTA buttons */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                    component="a"
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="contained"
                                    size="large"
                                    startIcon={<WhatsAppIcon />}
                                    fullWidth
                                    sx={{
                                        backgroundColor: '#25D366',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '1rem',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        py: 1.5,
                                        boxShadow: `0 4px 20px ${alpha('#25D366', 0.4)}`,
                                        '&:hover': {
                                            backgroundColor: '#1ebe5d',
                                            boxShadow: `0 6px 24px ${alpha('#25D366', 0.5)}`,
                                        },
                                    }}
                                >
                                    {translate('product.requestQuote') ?? 'בקש הצעת מחיר'}
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<ArrowBackIosNewIcon sx={{ transform: 'scaleX(-1)' }} />}
                                    onClick={() => navigate('/projects')}
                                    fullWidth
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderColor: accentColor,
                                        color: accentColor,
                                        '&:hover': { backgroundColor: alpha(accentColor, 0.06) },
                                    }}
                                >
                                    {translate('project.backToProjects') ?? 'חזרה לפרויקטים'}
                                </Button>
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>

                {/* ── Related projects ── */}
                {relatedProjects.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                        <Box sx={{ mt: 8, mb: 4, borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`, pt: 5 }}>
                            <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                                {translate('project.relatedProjects') ?? 'פרויקטים נוספים'}
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            {relatedProjects.map((rel, i) => {
                                const relName = translate(rel.name as any) ?? '';
                                const firstMedia = (rel.mediaUrls ?? [])[0];
                                const relHref = `/projects/${rel.id}`;
                                return (
                                    <Grid item xs={12} sm={6} md={3} key={rel.id ?? i}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.4, delay: i * 0.07 }}
                                            style={{ height: '100%' }}
                                        >
                                            <RouterLink to={relHref} style={{ textDecoration: 'none' }}>
                                                <Box
                                                    sx={{
                                                        borderRadius: 3,
                                                        overflow: 'hidden',
                                                        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                                                        '&:hover': {
                                                            boxShadow: `0 8px 28px ${alpha(accentColor, 0.2)}`,
                                                            transform: 'translateY(-4px)',
                                                        },
                                                    }}
                                                >
                                                    {/* Thumbnail */}
                                                    {firstMedia ? (
                                                        <Box sx={{ position: 'relative', height: 160, overflow: 'hidden', bgcolor: '#000' }}>
                                                            {isVideo(firstMedia) ? (
                                                                <>
                                                                    <Box
                                                                        component="video"
                                                                        src={firstMedia}
                                                                        muted
                                                                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                    />
                                                                    <Box sx={{
                                                                        position: 'absolute', inset: 0,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        background: 'rgba(0,0,0,0.3)',
                                                                    }}>
                                                                        <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
                                                                    </Box>
                                                                </>
                                                            ) : (
                                                                <Box
                                                                    component="img"
                                                                    src={firstMedia}
                                                                    alt={relName}
                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: 160,
                                                                background: `linear-gradient(135deg, ${alpha(accentColor, 0.15)} 0%, ${alpha(accentColor, 0.35)} 100%)`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Typography sx={{ fontSize: 48, opacity: 0.4 }}>🪵</Typography>
                                                        </Box>
                                                    )}
                                                    <Box sx={{ p: 2 }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight={700}
                                                            sx={{
                                                                color: theme.palette.text.primary,
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {relName}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </RouterLink>
                                        </motion.div>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </motion.div>
                )}
            </Container>
        </Box>
    );
};

export default ProjectDetailPage;
